import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ArrowRight, Maximize2, Minimize2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { GALLERY } from "../data/gallery";

const CAT_LABELS = {
  wtac: "WORLD TIME ATTACK CHALLENGE",
  unmarked: "UNMARKED",
  shoots: "CAR SHOOTS",
};

const PREVIEW_COUNT = 8;

function Lightbox({ items, index, onClose, onNav }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onNav]);

  const item = items[index];

  return (
    <motion.div
      data-testid="gallery-lightbox"
      className="fixed inset-0 z-[95] bg-ink/95 backdrop-blur-md flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <button
        data-testid="lightbox-close-button"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-6 right-6 z-10 w-12 h-12 flex items-center justify-center border border-white/25 text-white hover:border-signal hover:text-signal transition-colors bg-ink/60"
      >
        <X size={20} />
      </button>
      <button
        data-testid="lightbox-prev-button"
        onClick={(e) => { e.stopPropagation(); onNav(-1); }}
        aria-label="Previous photo"
        className="absolute left-4 md:left-8 z-10 w-12 h-12 flex items-center justify-center border border-white/25 text-white hover:border-signal hover:text-signal transition-colors bg-ink/60"
      >
        <ChevronLeft size={22} />
      </button>
      <button
        data-testid="lightbox-next-button"
        onClick={(e) => { e.stopPropagation(); onNav(1); }}
        aria-label="Next photo"
        className="absolute right-4 md:right-8 z-10 w-12 h-12 flex items-center justify-center border border-white/25 text-white hover:border-signal hover:text-signal transition-colors bg-ink/60"
      >
        <ChevronRight size={22} />
      </button>

      <motion.div
        className="max-w-[90vw] md:max-w-[80vw]"
        key={index}
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        <img
          data-testid="lightbox-image"
          src={item.full}
          alt={`${CAT_LABELS[item.category]} — frame ${index + 1}`}
          className="max-h-[80vh] max-w-full object-contain letterbox"
        />
        <div className="flex justify-between mt-4 font-mono text-[11px] tracking-[0.3em] text-smoke">
          <span>{CAT_LABELS[item.category]}</span>
          <span data-testid="lightbox-counter">
            {String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
          </span>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function GallerySection() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(null);

  const items = expanded ? GALLERY : GALLERY.slice(0, PREVIEW_COUNT);
  const nav = (dir) =>
    setOpen((i) => (i === null ? null : (i + dir + items.length) % items.length));

  return (
    <section id="gallery" data-testid="home-gallery" className="relative bg-ink py-24 md:py-36">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <Reveal>
          <div className="flex items-end justify-between mb-14 md:mb-20">
            <div>
              <p className="font-mono text-[10px] tracking-[0.4em] text-signal mb-4">02 — GALLERY</p>
              <h2
                className="font-display text-5xl md:text-7xl text-white leading-[0.95]"
                style={{ fontWeight: 900 }}
              >
                STILL <span className="text-outline">FRAMES</span>
              </h2>
            </div>
            <p className="hidden md:block font-mono text-[11px] tracking-[0.25em] text-smoke text-right">
              {GALLERY.length} FRAMES — 2026 SEASON
              <br />
              <span className="text-white/50">CLICK A FRAME TO EXPAND</span>
            </p>
          </div>
        </Reveal>

        <div className="relative">
          <div className="columns-2 md:columns-3 xl:columns-4 gap-3">
            {items.map((item, i) => (
              <Reveal key={item.full} delay={(i % 4) * 0.04} className="mb-3 break-inside-avoid">
                <button
                  data-testid={`gallery-item-${i + 1}`}
                  onClick={() => setOpen(i)}
                  className="group relative block w-full overflow-hidden letterbox cursor-pointer"
                >
                  <img
                    src={item.thumb}
                    alt={`${CAT_LABELS[item.category]} — frame ${i + 1}`}
                    loading={expanded ? "lazy" : "eager"}
                    className="w-full transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]"
                  />
                  <span className="absolute inset-0 bg-signal/0 group-hover:bg-signal/15 transition-colors duration-300" />
                </button>
              </Reveal>
            ))}
          </div>

          {!expanded && (
            <>
              <div className="absolute bottom-0 inset-x-0 h-56 bg-gradient-to-t from-ink via-ink/70 to-transparent pointer-events-none" />
              <div className="absolute bottom-10 inset-x-0 flex justify-center pointer-events-none">
                <button
                  data-testid="home-gallery-expand"
                  onClick={() => setExpanded(true)}
                  className="pointer-events-auto group flex items-center gap-3 bg-ink/80 backdrop-blur-sm border border-white/25 font-mono text-[11px] tracking-[0.25em] text-white px-8 py-4 hover:border-signal hover:text-signal transition-colors duration-300"
                >
                  <Maximize2 size={14} />
                  EXPAND GALLERY — ALL {GALLERY.length} FRAMES
                </button>
              </div>
            </>
          )}
        </div>

        <AnimatePresence>
          {expanded && (
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center mt-12"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <button
                data-testid="home-gallery-page-link"
                onClick={() => navigate("/gallery")}
                className="group flex items-center justify-center gap-3 bg-signal text-white font-mono text-[11px] tracking-[0.25em] px-8 py-4 hover:bg-white hover:text-ink transition-colors duration-300"
              >
                OPEN FULL GALLERY PAGE
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                data-testid="home-gallery-collapse"
                onClick={() => setExpanded(false)}
                className="flex items-center justify-center gap-3 border border-white/25 font-mono text-[11px] tracking-[0.25em] text-white/80 px-8 py-4 hover:border-signal hover:text-signal transition-colors duration-300"
              >
                <Minimize2 size={14} />
                COLLAPSE
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {open !== null && (
          <Lightbox items={items} index={open} onClose={() => setOpen(null)} onNav={nav} />
        )}
      </AnimatePresence>
    </section>
  );
}
